import React from 'react';
import {
  Certificate, Buildings, ShieldCheck, SealCheck, CaretDown, ArrowRight, Info,
} from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const SmartContractTerm = () => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        className="inline-flex items-baseline gap-1 text-slate-900 underline decoration-dotted decoration-slate-300 underline-offset-2"
        data-testid="smart-contract-term"
      >
        smart contract
        <Info size={12} className="text-foreground-muted translate-y-[1px]" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-64 text-xs leading-relaxed" data-testid="smart-contract-tooltip">
      <span className="block text-[10px] uppercase tracking-[0.14em] text-foreground-muted mb-1.5">Smart contract</span>
      A digital set of rules recorded on the blockchain that helps administer the investment instrument and its
      defined rights.
    </PopoverContent>
  </Popover>
);

const STEPS = [
  {
    icon: Certificate,
    title: 'You invest in a security',
    body: (
      <>
        Your investment is issued as a tokenized security, giving you a defined economic interest in the investment
        instrument.
      </>
    ),
  },
  {
    icon: Buildings,
    title: 'Your security is linked to the property',
    body: null, // built dynamically (uses investor ownership ratio)
  },
  {
    icon: ShieldCheck,
    title: 'The property provides additional security',
    body: (
      <>
        A mortgage or other applicable security interest over the underlying property is established for the benefit of
        the investment instrument, creating an additional asset-backed protection layer beyond the contractual economic
        rights of the security.
      </>
    ),
  },
  {
    icon: SealCheck,
    title: 'Performance is independently verified',
    body: (
      <>
        The project&apos;s financial performance and relevant underlying financial information are subject to
        independent third-party review, providing additional transparency over the performance used to determine
        investor economics.
      </>
    ),
  },
];

const LAYERS = ['Tokenized Security', 'Smart Contract', 'Asset-backed Security', 'Independent Verification'];

/**
 * "How your investment works" - what am I buying and how is the instrument structured.
 * Shared by mobile (bottom sheet) and desktop (modal).
 */
export const InvestmentStructureContent = ({ investorPercent }) => (
  <div className="space-y-7" data-testid="investment-structure-content">
    <ol className="space-y-6 md:grid md:grid-cols-2 md:gap-7 md:space-y-0">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        return (
          <li key={step.title} className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-9 h-9 rounded-xl bg-primary/[0.07] flex items-center justify-center">
                <Icon size={18} className="text-primary" weight="duotone" />
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.16em] text-foreground-muted">Step {index + 1}</p>
              <p className="text-sm font-medium text-slate-900 mt-1">{step.title}</p>
              <p className="text-sm text-foreground-muted leading-relaxed mt-1.5">
                {index === 1 ? (
                  <>
                    The instrument participates in the financial performance of the underlying real estate according to
                    the investor ownership ratio shown for this project
                    {investorPercent ? ` (currently ${Number(investorPercent).toFixed(1)}%)` : ''}. If the value or
                    distributable performance of the asset changes, the economics of the instrument reflect the
                    applicable terms.
                  </>
                ) : index === 0 ? (
                  <>
                    Your investment is issued as a tokenized security, administered by a <SmartContractTerm />, giving
                    you a defined economic interest in the investment instrument.
                  </>
                ) : (
                  step.body
                )}
              </p>
            </div>
          </li>
        );
      })}
    </ol>

    {/* Four-layer summary */}
    <div className="rounded-2xl bg-[rgb(var(--background-subtle))]/70 px-5 py-6">
      <p className="text-[10px] uppercase tracking-[0.16em] text-foreground-muted">One investment</p>
      <p className="text-sm font-medium text-slate-900 mt-1">Four layers of structure</p>

      {/* Mobile: vertical */}
      <div className="mt-5 space-y-1 md:hidden">
        {LAYERS.map((layer, i) => (
          <div key={layer}>
            <div className="bg-white rounded-xl border border-black/[0.05] px-4 py-3 text-sm text-slate-900">
              {layer}
            </div>
            {i < LAYERS.length - 1 && (
              <div className="flex justify-center py-1">
                <CaretDown size={12} className="text-foreground-muted/60" weight="bold" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop: horizontal */}
      <div className="hidden md:flex items-center gap-2 mt-5">
        {LAYERS.map((layer, i) => (
          <React.Fragment key={layer}>
            <div className="flex-1 basis-0 bg-white rounded-xl border border-black/[0.05] px-3 py-3 text-center text-xs text-slate-900 leading-snug">
              {layer}
            </div>
            {i < LAYERS.length - 1 && <ArrowRight size={13} className="text-foreground-muted/60 flex-shrink-0" weight="bold" />}
          </React.Fragment>
        ))}
      </div>
    </div>

    <p className="text-xs text-foreground-muted leading-relaxed">
      The exact rights, security arrangements, priority and enforcement mechanisms applicable to each investment are
      defined in the relevant offering and security documentation. Investment capital and returns are not guaranteed.
    </p>
  </div>
);

export default InvestmentStructureContent;
